import errorWarning from "$assets/icons/error-warning.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { TextField } from "$components/rhf-fields";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import { updateInvoiceDocument } from "$routes/patient/[:patientId]/_lib/invoices-v2/update-invoice-document";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import {
  PayerType,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import { object, string } from "yup";

const REASON = "reason";

const validationSchema = object().shape({
  [REASON]: string().required(),
});

const useStyles = makeStyles((theme) => ({
  warningIcon: { marginRight: theme.spacing(2) },
}));

export const VoidInvoiceModalV2 = ({
  doc,
  open,
  onClose,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { getCurrentTime } = useServerTime();
  const user = useUser();

  const formMethods = useForm<{ reason: string }>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
  });
  const [busy, setBusy] = useState(false);

  const handleVoid = async (values: { reason: string }) => {
    setBusy(true);
    try {
      const timestamp = getCurrentTime().toISOString();
      const transactionId = randomUniqueId();
      await updateInvoiceDocument(doc, {
        actionedByUserId: user.id,
        timestamp,
        update: (invoice) => {
          const current = invoice.toRecord();
          const patient = current.payers.find(
            (payer) => payer.type === PayerType.Patient,
          ) as (typeof current.payers)[number];
          return invoice
            .void({
              id: transactionId,
              uuid: null,
              type: TransactionType.Void,
              status: TransactionStatus.Success,
              timestamp,
              actionedByUserId: user.id,
              notes: values.reason,
              payerName: patient.name,
              payerType: PayerType.Patient,
            })
            .toRecord();
        },
      });
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          formatMessage({
            id: "invoicing.voidInvoice.error.msg",
            defaultMessage: "Failed to void this invoice",
          }),
        );
      }
    } finally {
      setBusy(false);
      onClose();
    }
  };

  const reasonLabel = formatMessage({
    id: "invoicing.void.reason.label",
    defaultMessage:
      "Please note reason for voiding invoice (max 150 characters)",
  });
  const reasonPlaceholder = formatMessage({
    id: "invoicing.void.reason.placeholder",
    defaultMessage: "Reason for voiding the invoice",
  });
  const handleErrorMessage = (value) =>
    value?.id ? formatMessage(value) : value;

  return (
    <DialogContainer open={open} onClose={onClose}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={errorWarning}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.void.modal.title"
              defaultMessage="You are about to void this invoice"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <FormProvider {...formMethods}>
        <form
          name="void-invoice-form"
          onSubmit={formMethods.handleSubmit(handleVoid)}
        >
          <DialogBody>
            <Grid container>
              <Typography variant="subtitle1">
                <FormattedMessage
                  id="invoicing.void.modal.subtitle"
                  defaultMessage="This process is not reversible. Are you sure you want to proceed?"
                />
              </Typography>
              <Typography variant="body2">
                <FormattedMessage
                  id="invoicing.void.modal.linkedOrdersNote"
                  defaultMessage="All orders linked to this invoice will move to Exempt so service delivery is not blocked."
                />
              </Typography>
              <Grid item xs={12}>
                <TextField
                  name={REASON}
                  label={reasonLabel}
                  placeholder={reasonPlaceholder}
                  disabled={busy}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  helperText={
                    formMethods.formState.touchedFields[REASON] &&
                    handleErrorMessage(formMethods.formState.errors[REASON])
                  }
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Box>
              <Button color="secondary" onClick={onClose} disabled={busy}>
                <FormattedMessage
                  id="invoicing.void.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button
                type="submit"
                color="error"
                data-testid="invoicing.void.accept"
                startIcon={<CheckIcon />}
                disabled={
                  formMethods.formState.isSubmitting ||
                  !formMethods.formState.isValid
                }
                loading={busy}
              >
                <FormattedMessage
                  id="invoicing.void.confirm"
                  defaultMessage="Void Invoice"
                />
              </Button>
            </Box>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};
